/* eslint-disable react/jsx-no-duplicate-props */
/* eslint-disable max-lines */
/* eslint-disable @typescript-eslint/no-unused-vars, no-unused-vars */
import React from 'react';
import { DSTabs, DSTab, TAB_TYPES } from '../index.js';
import type { DSTabsT, DSTabT } from '../index.js';

// test we expose the namespace and the namespace follows our deliverable conventions
type ComponentPropsForTabs = DSTabsT.Props;
type ComponentPropsTabsInternals = DSTabsT.InternalProps;
type ComponentPropsTabsDefaultProps = DSTabsT.DefaultProps;
type ComponentPropsTabsOptionalProps = DSTabsT.OptionalProps;

type ComponentPropsForTab = DSTabT.Props;
type ComponentPropsTabDefaultProps = DSTabT.DefaultProps;
type ComponentPropsTabOptionalProps = DSTabT.OptionalProps;
type ComponentPropsTabRequiredProps = DSTabT.RequiredProps;

const buttonRef = React.createRef() as React.MutableRefObject<HTMLButtonElement>;
const divRef = React.createRef() as React.MutableRefObject<HTMLDivElement>;
const MockComponent: React.ComponentType = () => <div></div>;

// Tabs
const testTabsOptionalProps: ComponentPropsTabsOptionalProps = {
  activeTab: '',
  innerRef: divRef,
  firstSubtabRef: buttonRef,
  lastTabRef: buttonRef,
  TabBarExtraContent: MockComponent,
  tabBarExtraContent: '',
};

// Tab
const testTabRequiredProps: ComponentPropsTabRequiredProps = {
  children: <div></div>,
};

const testTabOptionalProps: ComponentPropsTabOptionalProps = {
  disabled: false,
  onClick: () => {},
  onKeyDown: () => {},
  ref: buttonRef,
  required: true,
  style: {},
  title: '',
};

// difference Props and InternalProps is that InternalProps has all the default props filled in
// Props allows for partial defaults

// Tabs
const testTabsPartialDefaults: Partial<ComponentPropsTabsDefaultProps> = {
  allowTextSelection: false,
  isDSMobile: false,
  onTabChange: (tabId: string) => {},
  type: TAB_TYPES.NORMAL,
  showSeparator: true,
};

const testTabsProps: ComponentPropsForTabs = {
  ...testTabsOptionalProps,
  ...testTabsPartialDefaults,
};

const testTabsPropsAsSyntax = {
  ...testTabsOptionalProps,
  ...testTabsPartialDefaults,
} as ComponentPropsForTabs;

// Tab
const testTabPartialDefaults: Partial<ComponentPropsTabDefaultProps> = {
  tabId: '',
};

const testTabProps: ComponentPropsForTab = {
  ...testTabRequiredProps,
  ...testTabOptionalProps,
  ...testTabPartialDefaults,
};

const testTabPropsAsSyntax = {
  ...testTabRequiredProps,
  ...testTabOptionalProps,
  ...testTabPartialDefaults,
} as ComponentPropsForTab;

// InternalProps requires all defaults to be filled in

// Tabs
const testTabsCompleteDefaults: Required<ComponentPropsTabsDefaultProps> = {
  containerProps: {},
  animated: true,
  enableMouseEvents: false,
  allowTextSelection: false,
  onTabChange: (tabId: string) => {},
  tabsListAriaLabel: 'Tab list',
  type: TAB_TYPES.NORMAL,
  onlyRenderActiveTab: false,
  withCarousel: false,
  isDSMobile: false,
  showSelectionIndicator: true,
  children: [],
  fixedTabsHeaders: false,
  showSeparator: true,
  tabPanelsProps: {},
  swipeableViewsContainerProps: {},
};

const testTabsInternalProps: ComponentPropsTabsInternals = {
  ...testTabsOptionalProps,
  ...testTabsCompleteDefaults,
};

const testTabsInternalPropsAsSyntax = {
  ...testTabsOptionalProps,
  ...testTabsCompleteDefaults,
} as ComponentPropsTabsInternals;

// Tab
const testTabCompleteDefaults: Required<ComponentPropsTabDefaultProps> = {
  tabId: '',
  applyAriaDisabled: false,
};

// using the explicit type definition, if there is an error, it will be marked on the key that is wrong

// Tabs
const testTabsExplicitDefinition: ComponentPropsForTabs = {
  containerProps: {},
  animated: true,
  enableMouseEvents: false,
  allowTextSelection: false,
  onTabChange: (tabId: string) => {},
  tabsListAriaLabel: 'Tab list',
  type: TAB_TYPES.NORMAL,
  onlyRenderActiveTab: false,
  withCarousel: false,
  isDSMobile: false,
  showSelectionIndicator: true,
  children: [],
  fixedTabsHeaders: false,
  showSeparator: true,
  tabPanelsProps: {},
  swipeableViewsContainerProps: {},
  activeTab: '',
  innerRef: divRef,
  firstSubtabRef: buttonRef,
  lastTabRef: buttonRef,
  TabBarExtraContent: MockComponent,
  tabBarExtraContent: '',
};

// Tab
const testTabExplicitDefinition: ComponentPropsForTab = {
  children: [],
  disabled: false,
  onClick: () => {},
  onKeyDown: () => {},
  ref: buttonRef,
  required: true,
  style: {},
  title: '',
  tabId: '',
  applyAriaDisabled: false,
};

// using the "as" syntax, if there is an error, it will be marking the whole object as wrong because it is not compatible with the type

// Tabs
const testTabsInferedTypeCompatibility = {
  containerProps: {},
  animated: true,
  enableMouseEvents: false,
  allowTextSelection: false,
  onTabChange: (tabId: string) => {},
  tabsListAriaLabel: 'Tab list',
  type: TAB_TYPES.NORMAL,
  onlyRenderActiveTab: false,
  withCarousel: false,
  isDSMobile: false,
  showSelectionIndicator: true,
  children: [],
  fixedTabsHeaders: false,
  showSeparator: true,
  tabPanelsProps: {},
  swipeableViewsContainerProps: {},
  activeTab: '',
  innerRef: divRef,
  firstSubtabRef: buttonRef,
  lastTabRef: buttonRef,
  TabBarExtraContent: MockComponent,
  tabBarExtraContent: '',
} as ComponentPropsForTabs;

const testTabsDefinitionAsConst = {
  containerProps: {},
  animated: true,
  enableMouseEvents: false,
  allowTextSelection: false,
  onTabChange: (tabId: string) => {},
  tabsListAriaLabel: 'Tab list',
  type: TAB_TYPES.NORMAL,
  onlyRenderActiveTab: false,
  withCarousel: false,
  isDSMobile: false,
  showSelectionIndicator: true,
  children: [],
  fixedTabsHeaders: false,
  showSeparator: true,
  tabPanelsProps: {},
  swipeableViewsContainerProps: {},
  activeTab: '',
  innerRef: divRef,
  firstSubtabRef: buttonRef,
  lastTabRef: buttonRef,
  TabBarExtraContent: MockComponent,
  tabBarExtraContent: '',
} as const;

// Tab
const testTabInferedTypeCompatibility = {
  children: <div></div>,
  disabled: false,
  onClick: () => {},
  onKeyDown: () => {},
  ref: buttonRef,
  required: true,
  style: {},
  title: '',
  tabId: '',
  applyAriaDisabled: false,
} as ComponentPropsForTab;

const testTabDefinitionAsConst = {
  children: <div></div>,
  disabled: false,
  onClick: () => {},
  onKeyDown: () => {},
  ref: buttonRef,
  required: true,
  style: {},
  title: '',
  tabId: '',
  applyAriaDisabled: false,
} as const;

const ExampleUsageComponent = () => (
  <>
    {/* works with explicitly casted props, all syntaxes */}
    <DSTabs {...testTabsExplicitDefinition} />
    <DSTabs {...testTabsInferedTypeCompatibility}></DSTabs>
    <DSTabs {...testTabsDefinitionAsConst}></DSTabs>

    <DSTab {...testTabExplicitDefinition} />
    <DSTab {...testTabInferedTypeCompatibility} />
    <DSTab {...testTabDefinitionAsConst} />

    {/* works with inline values */}
    <DSTabs
      containerProps={{}}
      animated
      enableMouseEvents={false}
      allowTextSelection={false}
      onTabChange={(tabId: string) => {}}
      tabsListAriaLabel="Tab list"
      type={TAB_TYPES.NORMAL}
      onlyRenderActiveTab={false}
      withCarousel={false}
      isDSMobile={false}
      showSelectionIndicator
      fixedTabsHeaders={false}
      showSeparator
      tabPanelsProps={{}}
      swipeableViewsContainerProps={{}}
      activeTab=""
      innerRef={divRef}
      firstSubtabRef={buttonRef}
      lastTabRef={buttonRef}
      TabBarExtraContent={MockComponent}
      tabBarExtraContent=""
    >
      {[]}
    </DSTabs>

    <DSTab
      disabled={false}
      onClick={() => {}}
      onKeyDown={() => {}}
      ref={buttonRef}
      required
      style={{}}
      title=""
      tabId=""
      applyAriaDisabled={false}
    >
      <div></div>
    </DSTab>
  </>
);
